/**
 * AddBuildingVerifyPanel: Step 2 of the Add Building flow. A structured Verify
 * shell replacing the prior 3-input form.
 *
 * Source of truth: /tmp/design-handoff/carbon-clear/project/
 * add-building-flow.jsx::VerifyShell / VerifyField / ConfidenceBar /
 * A3_VerifyClean (lines 395-557).
 *
 * Confidence tiers, until the multi-source BE lands:
 *   "high"     every field has a source label, none are null, and the source
 *              set includes an owner-attested tag (owner / owner_espm / ll84 /
 *              tracker_owner_attested).
 *   "review"   a field has conflicting source values. Placeholder until step 4
 *              (multi-source persistence) wires conflict detection.
 *   "estimate" every field from a single city source, typically PLUTO at Add
 *              Building time. Copy: "City estimate · numbers from NYC tax records".
 *
 * Each VerifyField shows its value as a serif 22px display; the ✎ pencil swaps
 * that one field for an inline input, and Save returns to display mode with a
 * "manually entered" pill replacing the source pill(s).
 */
import React, { useState } from "react";
import { Pencil, Check, X } from "lucide-react";
import { cn } from "@/lib/utils";
import MonoLabel from "@/components/design/MonoLabel";

// The design's 3 tiers, labels lifted verbatim.
type Confidence = "high" | "review" | "estimate";

interface ConfidenceBarProps {
  confidence: Confidence;
  /** Count for the description copy ("1 field disagrees" vs "All 4 fields agree"). */
  conflictCount?: number;
  /** Switches the "high" copy off "All sources agree on every field", which
   *  would claim PLUTO/LL84 agreement on a value the user just typed. */
  userOverrodeAny?: boolean;
}

const ConfidenceBar: React.FC<ConfidenceBarProps> = ({
  confidence,
  conflictCount,
  userOverrodeAny,
}) => {
  const map: Record<
    Confidence,
    {
      label: string;
      desc: string;
      barsFilled: number;
      // Tailwind tokens as className strings, so dark mode + a brand flip carry.
      labelClass: string;
      bgClass: string;
      borderClass: string;
      dashed: boolean;
      pipFillClass: string;
    }
  > = {
    high: {
      label: "High confidence",
      desc: userOverrodeAny
        ? "You've confirmed the values — ready to add."
        : "All sources agree on every field.",
      barsFilled: 3,
      labelClass: "text-teal",
      bgClass: "bg-teal-soft",
      borderClass: "border-teal-border",
      dashed: false,
      pipFillClass: "bg-teal",
    },
    review: {
      label: "Worth a look",
      desc:
        conflictCount && conflictCount > 0
          ? `${conflictCount} field${
              conflictCount === 1 ? "" : "s"
            } disagree across sources — pick what looks right.`
          : "Sources disagree on at least one field — pick what looks right.",
      barsFilled: 2,
      labelClass: "text-ink",
      bgClass: "bg-paper-DEFAULT",
      borderClass: "border-ink",
      dashed: true,
      pipFillClass: "bg-ink",
    },
    estimate: {
      label: "City estimate",
      desc: "Numbers come from NYC tax records (PLUTO). Confirm to attest.",
      barsFilled: 1,
      labelClass: "text-ink-mute",
      bgClass: "bg-paper-panel",
      borderClass: "border-ink-line",
      dashed: false,
      pipFillClass: "bg-ink-mute",
    },
  };
  const m = map[confidence];
  return (
    <div
      className={cn(
        "flex items-center gap-4 px-4 py-3 rounded-lg border",
        m.bgClass,
        m.borderClass,
        m.dashed && "border-dashed",
      )}
    >
      <div className="flex gap-[3px]">
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            className={cn(
              "w-[18px] h-[5px] rounded-[2px]",
              i < m.barsFilled ? m.pipFillClass : "bg-ink/10",
            )}
          />
        ))}
      </div>
      <div className="min-w-0">
        <div className={cn("text-[13px] font-semibold", m.labelClass)}>
          {m.label}
        </div>
        <div className="text-xs text-ink-body mt-0.5">{m.desc}</div>
      </div>
    </div>
  );
};

// ─── VerifyField: one row per identity field ────────────────────────────

export interface VerifyFieldSource {
  /** Display label for the source pill, e.g. "PLUTO · BldgArea". */
  label: string;
  /** Whether this source is owner-attested (drives pill tone). */
  isOwnerAttested?: boolean;
}

export interface VerifyFieldSpec {
  /** Stable key used by the parent to track edits per field. */
  key: string;
  /** Left-column label (e.g. "Gross floor area"). */
  label: string;
  /** Sub-label / helper text in the left column. */
  helper?: string;
  /** Display string for the value (already formatted). */
  display: string;
  /** Raw value used to seed the inline edit input on pencil click. */
  raw: string;
  /** Provenance pills: one per source that contributed. */
  sources: VerifyFieldSource[];
  /** Renders the "warn" pill tone + dashed outline, for the conflict picker's
   *  anchor row (step 4, once the multi-source BE lands). */
  conflict?: boolean;
  /** Numeric input mode for the edit textbox. */
  numeric?: boolean;
  /** Suffix for the displayed value (e.g. " sq ft"). */
  unit?: string;
  /** Makes the edit affordance a ``<select>``, not free text. Used for
   *  ``primaryFunction`` so the ENERGY STAR Property Type vocabulary is one
   *  source of truth across Add Building and /portal/buildings/<id>/: free text
   *  attests "asdf" and silently breaks LL97 cap lookup, keyed on that string. */
  options?: readonly string[];
}

// `key` is reserved by React, so the child receives `fieldKey`; the parent remaps.
type VerifyFieldRest = Omit<VerifyFieldSpec, "key">;
interface VerifyFieldProps extends VerifyFieldRest {
  fieldKey: string;
  onSave: (key: string, value: string) => void;
}

const VerifyField: React.FC<VerifyFieldProps> = ({
  fieldKey,
  label,
  helper,
  display,
  raw,
  sources,
  conflict,
  numeric,
  unit,
  options,
  onSave,
}) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(raw);

  const commit = () => {
    onSave(fieldKey, draft);
    setEditing(false);
  };
  const cancel = () => {
    setDraft(raw);
    setEditing(false);
  };

  return (
    <div
      className={cn(
        "py-4 border-b border-ink-line grid items-start gap-5",
        "grid-cols-[180px_1fr_28px]",
      )}
    >
      <div>
        <div className="text-[13px] font-medium text-ink mb-1.5">{label}</div>
        {helper ? (
          <div className="text-[11px] text-ink-mute leading-[1.45]">
            {helper}
          </div>
        ) : null}
      </div>

      <div className="min-w-0">
        {editing ? (
          <div className="flex items-center gap-2">
            {options ? (
              <select
                autoFocus
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") commit();
                  if (e.key === "Escape") cancel();
                }}
                aria-label={`Edit ${label}`}
                data-testid={`verify-edit-input-${fieldKey}`}
                className="flex-1 h-9 px-3 text-[15px] text-ink border border-ink rounded-lg focus:outline-none focus:ring-2 focus:ring-teal/30 bg-paper"
              >
                {/* Honor a seeded value outside the canonical list: PLUTO's
                    BldgClass translation produces some the static list does not
                    enumerate. Picking any other option normalizes the row. */}
                {draft && !options.includes(draft) ? (
                  <option value={draft}>{draft}</option>
                ) : null}
                {options.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            ) : (
              <input
                type="text"
                inputMode={numeric ? "numeric" : "text"}
                autoFocus
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") commit();
                  if (e.key === "Escape") cancel();
                }}
                aria-label={`Edit ${label}`}
                data-testid={`verify-edit-input-${fieldKey}`}
                className="flex-1 h-9 px-3 text-[15px] text-ink border border-ink rounded-lg focus:outline-none focus:ring-2 focus:ring-teal/30"
              />
            )}
            <button
              type="button"
              onClick={commit}
              aria-label="Save"
              className="h-9 w-9 inline-flex items-center justify-center rounded-md bg-teal text-paper hover:bg-teal/90"
            >
              <Check className="h-4 w-4" aria-hidden="true" />
            </button>
            <button
              type="button"
              onClick={cancel}
              aria-label="Cancel"
              className="h-9 w-9 inline-flex items-center justify-center rounded-md text-ink-mute hover:text-ink"
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>
        ) : (
          <>
            <div
              className="font-display text-[22px] font-normal tracking-[-0.3px] text-ink mb-1.5"
              data-testid={`verify-value-${fieldKey}`}
            >
              {display}
              {unit ? (
                <span className="text-ink-mute font-sans text-sm font-normal">
                  {" "}
                  {unit}
                </span>
              ) : null}
            </div>
            <div className="flex flex-wrap gap-1.5">
              {sources.map((s, i) => {
                const warn = !!conflict;
                return (
                  <span
                    key={i}
                    className={cn(
                      "inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.5px] px-[7px] py-[3px] rounded",
                      warn
                        ? "border border-dashed border-ink bg-paper text-ink"
                        : "border border-teal-border bg-teal-soft text-teal",
                    )}
                  >
                    <span
                      className={cn(
                        "h-1.5 w-1.5 rounded-full",
                        warn ? "bg-ink" : "bg-teal",
                      )}
                      aria-hidden="true"
                    />
                    {s.label}
                  </span>
                );
              })}
            </div>
          </>
        )}
      </div>

      <div className="self-start">
        {!editing ? (
          <button
            type="button"
            onClick={() => setEditing(true)}
            aria-label={`Edit ${label}`}
            data-testid={`verify-edit-${fieldKey}`}
            className="h-7 w-7 inline-flex items-center justify-center text-ink-mute hover:text-ink"
          >
            <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
          </button>
        ) : null}
      </div>
    </div>
  );
};

// ─── VerifyShell: the whole Step-2 modal body ───────────────────────────

interface VerifyShellProps {
  buildingName: string;          // "112 MADISON AVENUE, New York"
  bin?: string | null;
  bbl?: string | null;
  borough?: string | null;
  confidence: Confidence;
  conflictCount?: number;
  /** To ``ConfidenceBar``: true once a field was overridden via the pencil,
   *  swapping "high" copy to "you've confirmed the values" so the panel does not
   *  claim server agreement. */
  userOverrodeAny?: boolean;
  fields: VerifyFieldSpec[];
  footerNote: string;
  primaryLabel: string;          // "Looks right — add building"
  onPrimary: () => void;
  onCityEstimates: () => void;
  onShareEspm?: () => void;
  isSubmitting?: boolean;
  /** Hard-disables the "Looks right — add building" CTA, with subtext below;
   *  "Add with City estimates" stays enabled. Set on ``confidence: "missing"``,
   *  where stamping ``tracker_owner_attested`` on empty fields would lie in the
   *  audit trail. */
  primaryDisabled?: boolean;
  /** Tooltip-style hint rendered under the disabled primary CTA. */
  primaryDisabledReason?: string;
  /** Called whenever a VerifyField inline edit is committed. */
  onFieldEdit: (key: string, value: string) => void;
}

export const AddBuildingVerifyPanel: React.FC<VerifyShellProps> = ({
  buildingName,
  bin,
  bbl,
  borough,
  confidence,
  conflictCount,
  userOverrodeAny,
  fields,
  footerNote,
  primaryLabel,
  onPrimary,
  onCityEstimates,
  onShareEspm,
  isSubmitting = false,
  primaryDisabled = false,
  primaryDisabledReason,
  onFieldEdit,
}) => {
  const idLine = [
    bin ? `BIN ${bin}` : null,
    bbl ? `BBL ${bbl}` : null,
    borough || null,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <>
      <div className="px-8 pt-6 pb-4 border-b border-ink-line">
        <MonoLabel className="mb-1">Verify · Looks right?</MonoLabel>
        <h2 className="font-display text-[28px] font-normal leading-tight tracking-[-0.6px] text-ink mt-1.5 mb-1">
          {buildingName}
        </h2>
        {idLine ? (
          <div className="font-mono text-[11px] tracking-[0.5px] text-ink-mute mb-4">
            {idLine}
          </div>
        ) : null}
        <ConfidenceBar
          confidence={confidence}
          conflictCount={conflictCount}
          userOverrodeAny={userOverrodeAny}
        />
      </div>

      <div className="px-8 pt-1">
        {fields.map((f) => {
          const { key: fkey, ...rest } = f;
          return (
            <VerifyField
              key={fkey}
              {...rest}
              // A non-React prop: React consumes `key` and does not forward it.
              fieldKey={fkey}
              onSave={onFieldEdit}
            />
          );
        })}
      </div>

      <div className="px-8 pt-4 pb-5">
        <p className="text-[11px] text-ink-mute leading-[1.55] mb-4">
          {footerNote}
        </p>
        <button
          type="button"
          onClick={onPrimary}
          disabled={isSubmitting || primaryDisabled}
          data-testid="verify-primary"
          className="block w-full text-center bg-teal text-paper px-0 py-[13px] rounded-lg font-semibold text-sm hover:bg-teal/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-teal/40 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {primaryLabel}
        </button>
        {primaryDisabled && primaryDisabledReason ? (
          <p
            data-testid="verify-primary-disabled-reason"
            className="text-[11px] text-ink-mute text-center mt-1.5 leading-[1.45]"
          >
            {primaryDisabledReason}
          </p>
        ) : null}
        <button
          type="button"
          onClick={onCityEstimates}
          disabled={isSubmitting}
          data-testid="verify-city-estimates"
          className="block w-full mt-2.5 text-center text-ink px-0 py-2.5 font-medium text-[13px] hover:underline disabled:opacity-60"
        >
          Add with City estimates
        </button>
        {onShareEspm ? (
          <button
            type="button"
            onClick={onShareEspm}
            disabled={isSubmitting}
            data-testid="verify-share-espm"
            className="block w-full mt-0.5 text-center text-ink-mute px-0 py-1.5 text-xs hover:text-ink disabled:opacity-60"
          >
            I&apos;ll share ESPM instead →
          </button>
        ) : null}
      </div>
    </>
  );
};

export default AddBuildingVerifyPanel;
